import React, { useEffect, useState } from "react";

const API_URL = "https://uml.glass.sonel.org";
const PAGE_SIZE = 100;
const errorColumns = [
  ["Ecc E", "ecc_e"], ["Ecc N", "ecc_n"], ["Ecc U", "ecc_u"],
  ["Antenna dome", "antenna_dome"], ["Antenna type", "antenna_type"],
  ["Receiver type", "receiver_type"], ["Receiver not found", "receiver_not_found"],
  ["Invalid QC XML", "invalid_qc_xml"],
];

function dateOnly(value) {
  if (value == null || value === "") return "—";
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : String(value);
}

function ErrorIndicator({ value }) {
  const isError = value === 1 || value === "1" || value === true;
  const isOk = value === 0 || value === "0" || value === false;
  if (!isError && !isOk) return <span className="aurora-error-unknown">—</span>;
  return (

    <span
      className={`qc-status ${isError ? "qc-error" : "qc-ok"}`}
      title={isError ? "Metadata error detected" : "No error detected"}
      aria-label={isError ? "Error detected" : "No error"}
    >
      {isError ? "✕" : "✓"}
    </span>




  );
}

function makeDataUrl(marker, year, page) {
  const params = new URLSearchParams();
  if (marker !== "all") params.set("marker", `eq.${marker}`);
  if (year !== "all") params.set("year", `eq.${year}`);
  params.set("order", "marker.asc,reference_date.asc,rinex_id.asc");
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String(page * PAGE_SIZE));
  return `${API_URL}/sweden?${params.toString()}`;
}

export default function MoinstroisSearch() {
  const [filterOptions, setFilterOptions] = useState([]);
  const [optionsError, setOptionsError] = useState("");
  const [selectedMarker, setSelectedMarker] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [page, setPage] = useState(0);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // sweden_filters contains one DISTINCT (marker, year) pair per row.
  // It must be created and exposed by PostgREST (see sweden_filters.sql).
  useEffect(() => {
    const controller = new AbortController();
    async function loadFilterOptions() {
      try {
        const response = await fetch(`${API_URL}/sweden_filters?select=marker,year&order=marker.asc,year.desc`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data)) throw new Error("Unexpected filter response");
        setFilterOptions(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Filter options loading error:", error);
          setOptionsError("Unable to load filter choices. Check the sweden_filters REST endpoint.");
        }
      }
    }
    loadFilterOptions();
    return () => controller.abort();
  }, []);

  // The available years depend on the selected marker; no extra request needed.
  const markers = [...new Set(filterOptions.map((row) => row.marker).filter(Boolean))].sort();
  const years = [...new Set(filterOptions
    .filter((row) => selectedMarker === "all" || row.marker === selectedMarker)
    .map((row) => row.year)
    .filter((year) => year !== null && year !== undefined))]
    .sort((a, b) => Number(b) - Number(a));

  useEffect(() => {
    const controller = new AbortController();
    async function loadData() {
      setLoading(true);
      setErrorMsg("");
      setResults([]);
      setTotal(null);
      try {
        const response = await fetch(makeDataUrl(selectedMarker, selectedYear, page), {
          signal: controller.signal,
          headers: { Prefer: "count=exact" },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data)) throw new Error("Unexpected API response");
        if (controller.signal.aborted) return;
        setResults(data);
        const contentRange = response.headers.get("Content-Range");
        const totalMatch = contentRange && contentRange.match(/\/(\d+)$/);
        setTotal(totalMatch ? Number(totalMatch[1]) : null);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Metadata loading error:", error);
          setErrorMsg("Unable to load station data. Please try again later.");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    loadData();
    return () => controller.abort();
  }, [selectedMarker, selectedYear, page]);

  function changeMarker(marker) {
    setSelectedMarker(marker);
    // Reset year if it does not exist for the newly selected marker.
    const validYears = filterOptions
      .filter((row) => marker === "all" || row.marker === marker)
      .map((row) => String(row.year));
    if (selectedYear !== "all" && !validYears.includes(selectedYear)) setSelectedYear("all");
    setPage(0);
  }

  const hasNext = total !== null
    ? (page + 1) * PAGE_SIZE < total
    : results.length === PAGE_SIZE;

  return (
    <div className="aurora-page">
      <section className="aurora-hero">
        <div className="aurora-eyebrow"><span className="aurora-status-dot" /> METADATA MONITORING</div>
        <h1><span className="sweden-flag" role="img" aria-label="Sweden">🇸🇪</span>{" "}
          Rinex with <span className="aurora-title-gradient">Metadata Errors In Sonel Repository</span>
        </h1>
        <p>GNSS station quality overview · Sweden</p>
      </section>

      <section className="aurora-panel" aria-label="Station metadata errors">
        <div className="aurora-panel-heading">
          <div><div className="aurora-panel-kicker">STATION REPORT</div><h2>Metadata anomalies by RINEX file</h2></div>
          <div className="aurora-counter" aria-live="polite">
            <span className="aurora-status-dot" />
            {loading ? "Loading…" : `${total ?? `${results.length}+`} RINEX`}
          </div>
        </div>

        <div className="aurora-filters" aria-label="Filters">
          <label className="aurora-filter"><span>MARKER</span>
            <select value={selectedMarker} onChange={(e) => changeMarker(e.target.value)}>
              <option value="all">All stations</option>
              {markers.map((marker) => <option key={marker} value={marker}>{marker}</option>)}
            </select>
          </label>
          <label className="aurora-filter"><span>YEAR</span>
            <select value={selectedYear} onChange={(e) => { setSelectedYear(e.target.value); setPage(0); }}>
              <option value="all">All years</option>
              {years.map((year) => <option key={year} value={String(year)}>{year}</option>)}
            </select>
          </label>
          <button className="aurora-reset" type="button"
            disabled={selectedMarker === "all" && selectedYear === "all"}
            onClick={() => { setSelectedMarker("all"); setSelectedYear("all"); setPage(0); }}>
            Reset filters
          </button>
        </div>
        {optionsError && <p className="aurora-filters-warning" role="alert">{optionsError}</p>}
        {loading && <p className="aurora-message" role="status">Loading station data…</p>}
        {errorMsg && <p className="aurora-message aurora-error" role="alert">{errorMsg}</p>}
        {!loading && !errorMsg && results.length === 0 && <p className="aurora-message">No metadata errors found.</p>}
        {!loading && !errorMsg && results.length > 0 && (
          <div className="aurora-table-scroll" tabIndex="0" role="region" aria-label="Scrollable station errors table">
            <table className="aurora-table">
              <thead><tr>
                <th scope="col">Marker</th><th scope="col">Station</th><th scope="col">Date</th>
                <th scope="col">Relative path</th>
                {errorColumns.map(([label, field]) => <th key={field} scope="col">{label}</th>)}
              </tr></thead>
              <tbody>{results.map((row, index) => (
                <tr key={row.rinex_id ?? `${row.marker}-${row.reference_date}-${index}`}>
                  <td>{row.marker ?? "—"}</td>
                  <td>{row.station_name ?? "—"}</td>
                  <td>{dateOnly(row.reference_date)}</td>
                  <td className="aurora-path" title={row.relative_path ?? ""}>
                    {row.relative_path ?? "—"}
                  </td>
                  {errorColumns.map(([, field]) => <td className="aurora-error-cell" key={field}><ErrorIndicator value={row[field]} /></td>)}
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        <div className="aurora-panel-footer">
          <span>SONEL · GNSS NETWORK</span>
          <span><span className="aurora-legend-error">●</span> Error detected &nbsp; <span className="aurora-legend-ok">✓</span> No error · Scroll horizontally →</span>
        </div>
        <nav className="aurora-pagination" aria-label="Results pagination">
          <button type="button" disabled={loading || page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>← Previous</button>
          <span>{total === 0 ? "0 results" : `Page ${page + 1}${total !== null ? ` / ${Math.max(1, Math.ceil(total / PAGE_SIZE))}` : ""}`} · {PAGE_SIZE} per page</span>
          <button type="button" disabled={loading || !hasNext} onClick={() => setPage((p) => p + 1)}>Next →</button>
        </nav>
      </section>
    </div>
  );
}
